import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import InlineAlert from "src/grading/components/InlineAlert/InlineAlert";
import { LicenseStatus } from "src/models/hierarchy";
import { selectLicenseStatus } from "src/store/reducers/auth/authSlice";

export interface LicenseBannerProps {
  /**
   * A district's license status, for a page about that district (a platform admin viewing it).
   * Left out, the banner reads the signed-in user's own license from the permissions state.
   */
  status?: LicenseStatus | null;
}

/** True while the signed-in user's district license is suspended (R19, FR-043). */
export const useLicenseSuspended = () =>
  useSelector(selectLicenseStatus) === "suspended";

/**
 * The license-suspended banner (FR-043, R19). A suspended district is read-only: staff can sign in,
 * view results, print documents and export data, while new sessions, score entry and roster syncs
 * are refused. Renders nothing unless the license is suspended.
 */
export const LicenseBanner = ({ status }: LicenseBannerProps) => {
  const { t } = useTranslation();
  const own = useSelector(selectLicenseStatus);
  const forDistrict = status !== undefined;
  const current = forDistrict ? status : own;
  if (current !== "suspended") return null;
  return (
    <Box data-testid="license-banner" data-print-hide sx={{ mb: 2.5 }}>
      <InlineAlert
        banner
        severity="warning"
        title={t("license.suspendedTitle")}
      >
        {forDistrict
          ? t("license.districtSuspendedBody")
          : t("license.suspendedBody")}
      </InlineAlert>
    </Box>
  );
};

/** "Paused while the license is suspended", for session and station screens (FR-043). */
export const LicensePausedNotice = () => {
  const { t } = useTranslation();
  return (
    <Box data-testid="license-paused">
      <InlineAlert severity="info" title={t("license.paused")}>
        {t("license.pausedBody")}
      </InlineAlert>
    </Box>
  );
};

export default LicenseBanner;
